import type { ChatGeometry } from "@app/store/chat/ChatState";

import { CHAT_MIN_HEIGHT, CHAT_MIN_WIDTH } from "./consts";
import getChatArea from "./getChatArea";

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Brings a rect back inside the allowed area. Runs on every viewport resize and on
 * restored state, so a window saved on a large monitor lands on screen on a laptop.
 *
 * Size is clamped before position, because the legal position range depends on how
 * large the window ends up being.
 *
 * Clamping only ever pulls a window in, never pushes it out: reopening on a larger
 * monitor leaves the window where the user left it instead of growing it.
 */
const clampChatGeometry = (
  geometry: ChatGeometry,
  viewportWidth: number,
  viewportHeight: number
): ChatGeometry => {
  const area = getChatArea(viewportWidth, viewportHeight);

  // The minimums win on a viewport too small to hold them: a window that overflows the
  // nav-bar clearance is better than one collapsed to nothing.
  const height = clamp(
    geometry.height,
    CHAT_MIN_HEIGHT,
    Math.max(CHAT_MIN_HEIGHT, area.bottom - area.top)
  );
  const width = clamp(
    geometry.width,
    CHAT_MIN_WIDTH,
    Math.max(CHAT_MIN_WIDTH, area.right - area.left)
  );

  // Math.max on each upper bound stops the range from inverting in exactly that case,
  // where the window is legitimately larger than the area: it then pins to the area's
  // top-left corner rather than to a nonsensical position below its own minimum.
  return {
    ...geometry,
    height,
    left: clamp(geometry.left, area.left, Math.max(area.left, area.right - width)),
    top: clamp(geometry.top, area.top, Math.max(area.top, area.bottom - height)),
    width,
  };
};

export default clampChatGeometry;
